/**
 * appNavItems — pure builder for the bottom-dock navigation items shown in
 * App.vue.
 *
 * Why a separate module:
 * - App.vue depends on Vuetify, Vue Router, Vuex, and our header/footer
 *   components — mounting it in unit tests would either pull all of that in
 *   or require heavy stubs. Extracting the array-builder lets us unit-test
 *   role/feature-flag conditions without spinning up the whole app shell.
 * - The bottom dock has caused two regressions in the last sprint (Поиск
 *   shown to mentors, Аналитика routing). A pure function lets us pin the
 *   contract with regression tests that run in milliseconds.
 *
 * Tabs intentionally hard-coded:
 *   home       — always shown
 *   search     — каталог наставников (/AllMentors) временно доступен только
 *                админам (страница ещё не несёт смысла для обычных
 *                пользователей): показываем при mentorsCatalogEnabled && isAdmin
 *   calendar   — always shown
 *   analytics  — always shown in the dock; the destination /Statistics is
 *                itself mentor-gated by the router
 *   create     — always shown (renamed «Планирование», routes to the
 *                events-and-sessions hub at /CreateEntityPage)
 *
 * Routes are mirrored in frontend/src/router/index.ts — if a route here
 * changes, update the router too (and vice versa).
 */

export interface AppNavItem {
    id: 'home' | 'search' | 'calendar' | 'analytics' | 'create';
    text: string;
    route: string;
}

export interface BuildAppNavItemsOptions {
    /** store.getters.userRole — null when logged out or role unset. */
    userRole: string | null;
    /** Platform feature flag — when false, the public mentor catalog is hidden everywhere. */
    mentorsCatalogEnabled: boolean;
    /**
     * Оператор-админ ли текущий пользователь. Каталог наставников
     * (/AllMentors) временно доступен только админам, поэтому вкладка «Поиск»
     * показывается лишь им (см. useIsAdmin + route-guard на /AllMentors).
     */
    isAdmin: boolean;
}

export function buildAppNavItems({
    userRole,
    mentorsCatalogEnabled,
    isAdmin,
}: BuildAppNavItemsOptions): AppNavItem[] {
    const items: AppNavItem[] = [
        { id: 'home', text: 'Главная', route: '/dashboard' },
    ];

    // «Поиск» — каталог наставников. Страница /AllMentors пока не несёт смысла
    // для обычных пользователей → доступна только админам, поэтому и вкладку
    // показываем лишь им (и только если каталог вообще включён флагом).
    if (mentorsCatalogEnabled && isAdmin) {
        items.push({ id: 'search', text: 'Поиск', route: '/AllMentors' });
    }

    items.push({ id: 'calendar', text: 'Календарь', route: '/CalendarPage' });
    // Analytics destination is /Statistics; role access remains enforced by
    // the router. The dock itself stays visually consistent across roles.
    items.push({ id: 'analytics', text: 'Аналитика', route: '/Statistics' });
    // «Создать» → «Планирование»: bottom-dock label aligns with the new
    // terminology («Личные сессии», «Планирование событий»).
    items.push({ id: 'create', text: 'Планирование', route: '/CreateEntityPage' });

    return items;
}
